//#region format
export function pad(n: any) {
  return (v: any) => `${v}`.padStart(n, "0");
}

export const pad2 = /* @__PURE__ */ pad(2);
export const pad3 = /* @__PURE__ */ pad(3);

export function TsPrintToLog(ts: number) {
  const d = new Date(ts);
  return [
    pad2(d.getHours()),
    pad2(d.getMinutes()),
    pad2(d.getSeconds()),
    pad3(d.getMilliseconds()),
  ].join(":");
}

export function formatTime(seconds: number) {
  return [seconds / 60, seconds % 60].map((v) => pad2(Math.floor(v))).join(":");
}

export const lpad = (value: number, size: number, radix: number = 8) => {
  const val = value.toString(radix);
  const zeros = "0".repeat(size);
  return `${zeros}${val}`.substr(-size);
};

//#endregion

//#region dedent
const RegexIndentInit = /\r?\n([\t ]*)$/;
const RegexIndentLoop = /\n[\t ]+/g;
const RegexIntendEnd = /^\r?\n/;

/** dedent string literal */
export function dedent(inpt: TemplateStringsArray | string, ...values: any[]) {
  try {
    const matches = [];
    const strings = typeof inpt === "string" ? [inpt] : inpt.slice();

    strings[strings.length - 1] = strings[strings.length - 1].replace(RegexIndentInit, "");

    for (let i = 0; i < strings.length; i++) {
      let match;
      if ((match = strings[i].match(RegexIndentLoop))) matches.push(...match);
    }

    if (matches.length) {
      const size = Math.min(...matches.map((value) => value.length - 1));
      const pattern = new RegExp(`\n[\t ]{${size}}`, "g");

      for (let i = 0; i < strings.length; i++) {
        strings[i] = strings[i].replace(pattern, "\n");
      }
    }

    strings[0] = strings[0].replace(RegexIntendEnd, "");

    const res = strings
      .reduce((acc: string, p: string, i: number) => {
        return [acc, values[i - 1].toString(), p].join("");
      })
      .trim();

    return res;
  } catch (_e) {
    return "";
  }
}
//#endregion

//#region regex
/** regex repalce all with async fn */
export async function replaceAsync(
  str: string,
  regex: RegExp,
  asyncFn: (args: RegExpMatchArray) => Promise<string>
): Promise<string> {
  //@ts-ignore:
  const promises = ([...str.matchAll(regex)] || []).map((args) => {
    return asyncFn(args);
  });
  const data = await Promise.all(promises);
  return str.replace(regex, () => data.shift()!);
}
//#endregion

/** obj array to csv */
export function objToCSVString(obj: any[], sep = ",") {
  const header = Object.keys(obj[0]);
  const body = obj
    .map((r) => {
      return Object.values(r).join(sep);
    })
    .join("\n");

  return header.join(sep) + "\n" + body;
}

export function regexGetGroup<T = Record<string, string>>(s: string, r: RegExp) {
  const res = [...s.matchAll(r)];
  return res.map((rr) => rr.groups! as T);
}

export function StrSplitFirstLine(str: string) {
  const lb = str.indexOf("\n");
  if (lb === -1) {
    return [str, ""];
  } else {
    return [str.slice(0, str.indexOf("\n")), str.slice(str.indexOf("\n"))];
  }
}

export const splitStr = (str: string, idx: number[]) => {
  const res = [];
  for (let index = 1; index < idx.length; index++) {
    res.push(str.slice(idx[index - 1], idx[index]));
  }

  res.push(str.slice(idx[idx.length - 1]));
  return res;
};

//#region md

/** replaces all `[&, <, >, ", ']` to `[&amp, &lt, &gt, &quot, &apos]` */
export const escape_html = (s: string) => {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const md_elems = [
  "codeblock",
  "inlinecode",
  "image",
  "link",
  "headers",
  "bold",
  "em",
  "del",
  "blockquote",
  "hr",
  "ul",
  "ol",
  "p",
  "pre",
  "code",
  "comment",
] as const;

type md_e = (typeof md_elems)[number];

const md_regex: Record<md_e, RegExp> = {
  codeblock: /((\n\t)(.*))+/g,
  inlinecode: /(`)(.*?)\1/g,
  image: /!\[([^\[]+)\]\(([^\)]+)\)/g,
  link: /\[([^\[]+)\]\(([^\)]+)\)/g,
  headers: /(?:^|\n)(#+\s*)(.*)/g,
  bold: /(\*{2}|_{2})(.*?)\1/g,
  em: /(\*{1}|_{1})(.*?)\1/g,
  del: /(\~\~)(.*?)\1/g,
  blockquote: /(?:^|\n)(&gt;|\>)(.*)/g,
  hr: /(?:^|\n)((\-{3,})|(={3,}))/g,
  ul: /((?:^|\n)\s*(\-|\+)\s.*)+/g,
  ol: /((?:^|\n)\s*([0-9]+\.)\s.*)+/g,
  p: /(?:^|\n)+(?!<pre>)(?!<h)(?!<ul>)(?!<blockquote)(?!<hr)(?!\t)([^\n]+)\n/g,
  pre: /(?:^|\n)(<pre>)((\n|.)*)(<\/pre>)/g,
  code: /(?:^|\n):::((\n|.)*):::/g,
  comment: /(^---.*)/gm,
};

const list = /* @__PURE__ */ (t: string) => (m: any) => {
  return `
      ${m
        .trim()
        .split("\n")
        .reduce((p: any, c: any) => `${p}<li>${c.substring(2)}</li>`, `<${t}>`)}
    </${t}>
  `;
};

const md_repl_html_str: Record<md_e, (...s: string[]) => string> = /* @__PURE__ */ {
  codeblock: (text) => `<pre>${text}</pre>\n`,
  inlinecode: (text) => `<code>${text}</code>\n`,
  image: (_match, text, src) => `<img src="${src}" alt="${text}"/>\n`,
  link: (_match, text, href) => `<a href="${href}">${text}</a>\n`,
  headers: (_match, hs, text) => {
    return `\n<h${hs.trim().length} id="${text.trim().replaceAll(" ", "-")}">${text}</h${
      hs.trim().length
    }>\n`;
  },
  bold: (text) => `<strong>${text}</strong>\n`,
  em: (text) => `<em>${text}</em>\n`,
  del: (text) => `<del>${text}</del>\n`,
  blockquote: (text) => `<blockquote>${text.trim().slice(4)}</blockquote>\n`,
  hr: (e) => `<hr/>\n`,
  ul: /* @__PURE__ */ list("ul"),
  ol: /* @__PURE__ */ list("ol"),
  p: (_match, text) => `<p>${text}</p>\n`,
  pre: (_match, s, text, _l, e) => {
    return `
          ${text.split("\n").reduce((_p: any, n: any) => n.substring(1) + "\n", `${s}`)}
        ${e}\n
      `;
  },
  code: (_match, s, text, _l, e) => {
    return `
          ${text.split("\n").reduce((_p: any, n: any) => n.substring(1) + "\n", `${s}`)}
        ${e}\n
      `;
  },
  comment: (e) => "",
};

export const md_html = (str: string) => {
  return escape_html(str)
    .replace(md_regex.comment, md_repl_html_str.comment) // ignore comments/meta
    .replace(md_regex.codeblock, md_repl_html_str.codeblock) //codeblock
    .replace(md_regex.inlinecode, md_repl_html_str.inlinecode) //inlinecode
    .replace(md_regex.image, md_repl_html_str.image) //image
    .replace(md_regex.link, md_repl_html_str.link) //link
    .replace(md_regex.headers, md_repl_html_str.headers) // headers
    .replace(md_regex.bold, md_repl_html_str.bold) //bold
    .replace(md_regex.em, md_repl_html_str.em) //bold/ital
    .replace(md_regex.del, md_repl_html_str.del) // strike through
    .replace(md_regex.blockquote, md_repl_html_str.blockquote) // blockquote
    .replace(md_regex.hr, md_repl_html_str.hr) // hr
    .replace(md_regex.ul, md_repl_html_str.ul) // ul
    .replace(md_regex.ol, md_repl_html_str.ol) // ol
    .replace(md_regex.p, md_repl_html_str.p) // paragraph
    .replace(md_regex.pre, md_repl_html_str.pre);
};

//#endregion

export function bytes(...values: Array<number | string>) {
  const result: number[] = [];

  values.forEach((value) => {
    if (typeof value === "string") {
      result.push(...value.split("").map((c) => c.charCodeAt(0)));
    }

    if (typeof value === "number") {
      result.push(value);
    }
  });

  return new Uint8Array(result);
}
